import { Injectable, inject } from '@angular/core';
import { enlaceDeSeguimiento } from '@core/routing/rutas-matricula';
import { PaymentResponseStatus } from '@domain/payment/PaymentReponseStatus';
import { ApplicationService } from '@services/application.service';
import { traerSolicitud } from '@features/registro/solicitud';
import type { Cabecera } from '@features/seguimiento/cabecera';
import { CABECERAS, ESPERA_AGOTADA, IR_AL_ESTADO } from './copy-retorno';
import { Desenlace } from './desenlace';
import { OrigenDelPago, SalidaDelRetorno, SeguimientoDelPago } from './origen-del-pago';
import { RetornoDePago } from './retorno-pago.service';

/**
 * El pago de una **matricula nueva**, en su solicitud (SPEC-013).
 *
 * Es lo que la pantalla de retorno hacia a mano hasta SPEC-016, sin mas cambio
 * que el sitio: la solicitud lleva su `payment.paymentId`, y lo unico que se
 * escribe al volver es el estado que dijo el middleware.
 */
@Injectable({ providedIn: 'root' })
export class PagoDeSolicitud implements OrigenDelPago {
  private readonly solicitudes = inject(ApplicationService);
  private readonly retorno = inject(RetornoDePago);

  /**
   * Sin solicitud no se sabe ni de quien es el tramite, y sin `paymentId` no hay
   * pago que consultar. Las dos son *"no lo pudimos saber"*, y no *"el pago
   * fallo"*.
   */
  async buscar(id: string): Promise<string | null> {
    const solicitud = await traerSolicitud(this.solicitudes, id).catch(() => null);
    return solicitud?.payment?.paymentId ?? null;
  }

  /**
   * Se vuelve a leer la solicitud antes de anotar: entre consultar el pago y
   * escribir el resultado ha pasado toda la espera, y `updateApplication`
   * reemplaza el documento entero.
   *
   * **Si falla no se cuenta.** La pantalla ya esta diciendo el desenlace, que es
   * lo que la persona necesita; esto es una anotacion nuestra y la Logic App
   * llega igual.
   */
  async anotar(id: string, ultimo: PaymentResponseStatus): Promise<void> {
    const solicitud = await traerSolicitud(this.solicitudes, id).catch(() => null);
    if (solicitud) await this.retorno.anotar(solicitud, ultimo);
  }

  /** Al estado del tramite: ahi esta el radicado, y ahi aparece el cierre. */
  salida(id: string): SalidaDelRetorno {
    return { enlace: enlaceDeSeguimiento(id), etiqueta: IR_AL_ESTADO };
  }

  /**
   * **Ninguno: el numero de esta persona es el radicado**, y esta a un toque — la
   * salida de aqui lleva justo a la pantalla donde vive, con su linea de tiempo.
   * Ensenar ademas el del pago seria darle dos numeros y ningun criterio para
   * saber cual usar.
   */
  numeroDeSeguimiento(): SeguimientoDelPago | null {
    return null;
  }

  /** Los de SPEC-013, que se escribieron para este tramite. */
  cabeceras(): Record<Desenlace, Cabecera> {
    return CABECERAS;
  }

  esperaAgotada(): string {
    return ESPERA_AGOTADA;
  }
}
